import { useEffect, useRef, useState } from 'react';

/**
 * Hook pour détecter les changements de route et exécuter une callback
 * Utilise l'API native du navigateur pour être indépendant du contexte Router
 * Compatible avec Gatsby et fonctionne même en dehors du contexte Router
 * 
 * @param callback - Fonction appelée à chaque changement de path
 * @param immediate - Si true, exécute la callback immédiatement au montage (défaut: false)
 */
export const useRouteChange = (
  callback: (newPath: string, previousPath?: string) => void,
  immediate: boolean = false
) => {
  const [pathname, setPathname] = useState(() => 
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const previousPathRef = useRef<string>();
  const callbackRef = useRef(callback);

  // Garder la callback à jour
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentPath = window.location.pathname;
    const previousPath = previousPathRef.current;

    // Au premier montage
    if (previousPath === undefined) {
      if (immediate) {
        callbackRef.current(currentPath);
      }
    } else if (currentPath !== previousPath) {
      // Changement de route détecté
      callbackRef.current(currentPath, previousPath);
    }

    // Sauvegarder le path actuel pour la prochaine fois
    previousPathRef.current = currentPath;
    setPathname(currentPath);
  }, [immediate]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Surveiller les changements via popstate (bouton retour/avant)
    const handlePopState = () => {
      const newPath = window.location.pathname;
      if (newPath !== previousPathRef.current) {
        callbackRef.current(newPath, previousPathRef.current);
        previousPathRef.current = newPath;
        setPathname(newPath);
      }
    };

    // Surveiller les changements via mutation observer (navigation programmatique)
    let lastPath = window.location.pathname;
    const checkPathChange = () => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPath) {
        callbackRef.current(currentPath, lastPath);
        previousPathRef.current = currentPath;
        setPathname(currentPath);
        lastPath = currentPath;
      }
    };

    // Polling léger pour Gatsby (qui ne déclenche pas toujours popstate)
    const pathCheckInterval = setInterval(checkPathChange, 100);

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(pathCheckInterval);
    };
  }, []);

  return pathname;
};

export default useRouteChange; 